import type { RefObject } from 'react';
import { createContext, useCallback, useContext, useEffect } from 'react';

interface UseStickyFilterBarArgs {
	barRef: RefObject<HTMLElement>;
	sentinelRef: RefObject<HTMLElement>;
}

interface UseStickyFilterBarReturn {
	scrollToStartOfStickyBar: () => void;
}

export const useStickyFilterBar = ({ barRef, sentinelRef }: UseStickyFilterBarArgs): UseStickyFilterBarReturn => {
	useEffect(() => {
		const sentinel = sentinelRef.current;
		const bar = barRef.current;
		if (!sentinel || !bar) return undefined;

		// Used by the sticky background pseudo-element to avoid horizontal overflow on classic scrollbar systems.
		bar.style.setProperty('--scrollbar-width', `${window.innerWidth - document.documentElement.clientWidth}px`);

		const stickyObserver = new IntersectionObserver(
			([entry]) => {
				bar.toggleAttribute('data-stuck', !entry.isIntersecting);
			},
			{ threshold: [0] },
		);
		stickyObserver.observe(sentinel);

		return () => {
			stickyObserver.disconnect();
		};
	}, [barRef, sentinelRef]);

	const scrollToStartOfStickyBar = useCallback(() => {
		const sentinel = sentinelRef.current;
		const bar = barRef.current;

		// Only scroll when the bar is in sticky mode. If the user is near the
		// top (non-sticky bar visible), there's nothing to scroll to.
		if (!sentinel || !bar?.hasAttribute('data-stuck')) return;

		const top = sentinel.getBoundingClientRect().bottom + window.scrollY + 1;
		window.scrollTo({ behavior: 'instant' as ScrollBehavior, top });
	}, [barRef, sentinelRef]);

	return { scrollToStartOfStickyBar };
};

export const FilterBarScrollContext = createContext<() => void>(() => undefined);

export const useScrollToStartOfStickyBar = (): (() => void) => useContext(FilterBarScrollContext);
